import { useAuth0 } from '@auth0/auth0-react';
import { useState, useEffect, useRef } from 'react';
import { useStore } from '../store/store';

/**
 * Custom hook that manages Auth0 access token synchronization and related error handling.
 * This hook handles silent token refresh, error message display, and logout scenarios.
 */
const extensionId = process.env.REACT_APP_EXTENSION_ID;
export const useSyncAuth0AccessToken = () => {
	const { getAccessTokenSilently } = useAuth0();
	const auth0TokenId = useStore((state) => state.auth0TokenId);
	const setToken = useStore((state) => state.setToken);
	const [isTokenLoading, setIsTokenLoading] = useState(true);

	/**
	 * Effect for silently refreshing the access token
	 * Handles the "Login required" error by resetting cached languages
	 */
	// Add a ref to track if we've already attempted to get a token
	const hasAttemptedTokenRefresh = useRef(false);

	useEffect(() => {
		const runAsync = async () => {
			try {
				// Attempt to get a new access token without using cache
				const response = await getAccessTokenSilently({
					cacheMode: 'off',
					detailedResponse: true,
				});

				if (response && response.id_token) {
					setToken(response.id_token);
				}

			} catch (e: any) {
				// If login is required, reset language preferences
				if (e.message.startsWith('Login required')) {
					// If user session expired, send message to chrome extension to sync the logout state. This will automatically close the sidePanel and then  will require the user to login again in the chrome extension.
					if (window.chrome && window.chrome.runtime) {
						window.chrome.runtime.sendMessage(extensionId!, { type: 'appLogout', data: null }, () => {
							console.debug('Extension logout successful');
						});
					}
				}
			} finally {
				setIsTokenLoading(false);
			}
		};

		// Only run if we haven't attempted to refresh the token yet or if we don't have a token
		if (!hasAttemptedTokenRefresh.current || !auth0TokenId) {
			runAsync();
		}
	}, [getAccessTokenSilently, setToken]);

	return { isTokenLoading };
};
